import * as React from 'react';
import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { openConfirmModal } from '@mantine/modals';
import { useQuery, useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';

// API
import { GetManifestOrganizationById, PatchLoan, PostOrganizationLoan, queryClient } from '../../api';

// Components
import { FormScaffolding, FormHandle } from '../Form/FormScaffolding';
import { hydrateManifestSection } from '../Form/helpers/hydrateManifestSection';
import { Spinner } from '../Spinner';

// Contexts
import { RefetchSelfContext } from '../../contexts/RefetchSelf';

// Types
import { IFormSection, IManifestFieldHydrated } from '../../manifests/types';
import { IManifest, IOrganization } from '../../types';

interface LoanFormProps {
    loan?: IManifest;
    isNew?: boolean;
    clientId?: string;
    loanId?: string;
    onCancel?: () => void;
    onSave?: () => void;
    manifestSections?: IFormSection<IManifestFieldHydrated>[];
    ancillaryData?: Record<string, any>;
}

export const defaultLoanFormSections = [
    {
        manifest: hydrateManifestSection([
            { id: 'LOA0001' },
            { id: 'LOA0009' },
            { id: 'LOA0002' },
            // { id: 'LOA0003' },
            { id: 'LOA0004' },
            { id: 'LOA0005' },
            // { id: 'LOA0006' },
            // { id: 'LOA0008' },
            {},
            // { id: 'LOA0007' },
            { id: 'LOA0012' },
            { id: 'LOA0010' },
            { id: 'LOA0011' }
        ])
    }
] as IFormSection<IManifestFieldHydrated>[];

export type LoanFormHandle = {
    onCancel: () => void;
};

export const LoanForm = React.forwardRef<LoanFormHandle, LoanFormProps>(
    (
        { manifestSections = defaultLoanFormSections, loan, loanId, clientId, onCancel, ancillaryData, onSave, isNew },
        ref
    ) => {
        const FormRef = React.useRef<FormHandle>(null);
        const { refetch } = React.useContext(RefetchSelfContext);
        const { t } = useTranslation();
        const openDiscardChangesModal = React.useCallback(({ confirmCallback }: { confirmCallback: () => void }) => {
            openConfirmModal({
                title: t('modals.confirmDiscardChanges.title', { ns: 'components' }),
                children: <Text size="sm">{t('modals.confirmDiscardChanges.body', { ns: 'components' })}</Text>,
                labels: {
                    confirm: t('modals.confirmDiscardChanges.confirmCTA', { ns: 'components' }),
                    cancel: t('cancel', { ns: 'common' })
                },
                confirmProps: { color: 'red' },
                onCancel: () => console.log('Cancel'),
                onConfirm: () => {
                    confirmCallback();
                }
            });
        }, []);
        const patchLoan = useMutation(PatchLoan, {
            onSuccess: async (data, variables) => {
                queryClient.setQueryData<IManifest>(['loan', loanId], (oldData) => {
                    return data?.keyed ?? null;
                });
                refetch?.();
                notifications.show({
                    title: t('updatedLoan.title', {
                        ns: 'notifications'
                    }),
                    message: t('updatedLoan.message', {
                        ns: 'notifications'
                    })
                });
            },
            onError: (err: any) => {
                if (!err?.error?.showMessage) {
                    notifications.show({
                        color: 'red',
                        title: t('errorUpdatingLoan.title', {
                            ns: 'notifications'
                        }),
                        message: t('errorUpdatingLoan.message', {
                            ns: 'notifications'
                        })
                    });
                }
            }
        });
        const postLoan = useMutation(PostOrganizationLoan, {
            onSuccess: async (data, variables) => {
                if (clientId) {
                    queryClient.invalidateQueries({
                        queryKey: ['clientById', clientId],
                        exact: false,
                        refetchType: 'active'
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['clientRecordById', clientId],
                        exact: false,
                        refetchType: 'active'
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['loansById', clientId],
                        exact: false,
                        refetchType: 'active'
                    });
                }
                queryClient.invalidateQueries({ queryKey: ['loans'], exact: false, refetchType: 'active' });
                onSave?.();
                notifications.show({
                    title: t('createdLoan.title', {
                        ns: 'notifications'
                    }),
                    message: t('createdLoan.message', {
                        ns: 'notifications'
                    })
                });
            },
            onError: (err: any) => {
                if (!err?.error?.showMessage) {
                    notifications.show({
                        color: 'red',
                        title: t('errorCreatingLoan.title', {
                            ns: 'notifications'
                        }),
                        message: t('errorCreatingLoan.message', {
                            ns: 'notifications'
                        })
                    });
                }
            }
        });
        const onClickSave = React.useCallback(
            async (values: Record<string, any>) => {
                if (loanId) {
                    await patchLoan.mutate({
                        loanId: loanId,
                        loan: values ?? {}
                    });
                } else if (clientId) {
                    await postLoan.mutate({
                        clientId: clientId,
                        loan: values ?? {}
                    });
                }
                // onSave?.();
                // return Promise.resolve();
            },
            [loan, patchLoan, postLoan]
        );

        const onCancelEdit = React.useCallback(() => {
            if (FormRef?.current?.isDirty()) {
                openDiscardChangesModal({
                    confirmCallback: () => {
                        onCancel?.();
                        FormRef?.current?.reset();
                    }
                });
            } else {
                onCancel?.();
            }
        }, [FormRef]);

        React.useImperativeHandle(
            ref,
            () => ({
                onCancel: () => {
                    return onCancelEdit();
                }
            }),
            [onCancelEdit]
        );

        return (
            <>
                {!isNew && !loan ? (
                    <Spinner />
                ) : (
                    <FormScaffolding
                        ref={FormRef}
                        sections={manifestSections}
                        drawerCard
                        disableValidation={false}
                        ancillaryData={ancillaryData}
                        // key={client?.ORG0002}
                        data={loan}
                        onSave={async ({ values }) => {
                            await onClickSave(values);
                            onSave?.();
                        }}
                        onCancel={onCancelEdit}
                        externalIsLoading={patchLoan?.isLoading || postLoan?.isLoading}
                    />
                )}
            </>
        );
    }
);

export default LoanForm;
